import React, { useEffect, useState } from 'react'
import { Form, Row, Col, Select, Radio } from 'antd'
import RefinancingButton from '../RefinancingButton/RefinancingButton'
import { Datepicker, Input, InputMoney } from 'components'
import locale from 'antd/es/date-picker/locale/pt_BR'
import { simulationValues } from 'store/modules/simulation/actions'
import { useDispatch } from 'react-redux'
import { useSimulation } from 'hooks'
import { correctTimezone, timeAsDayjs } from 'utils/time'
import dayjs from 'dayjs'
import format from 'utils/format'
import { _personTypeEnum } from 'globals'
import { employmentType } from 'components/FormUserData/Income/types'

interface ICustomerDataValues {
  cpf?: string
  cnpj?: string
  name: string
  birthdate?: dayjs.Dayjs | string
  foundationDate?: dayjs.Dayjs | string
  email: string
  phone: string
  employmentType: employmentType
  income: number
  personType?: _personTypeEnum
  currentStep?: number
  simulationId?: string
}

export default function CustomerData () {
  const [formRef] = Form.useForm()
  const simulation = useSimulation()
  const dispatch = useDispatch()
  const [personType, setPersonType] = useState<_personTypeEnum>('fisica')

  useEffect(() => {
    function onFill () {
      const data = Object.assign({}, simulation.values) || {}

      if (data.birthdate && dayjs(data.birthdate).isValid()) data.birthdate = timeAsDayjs(data.birthdate, { applyTimezone: false }).format('DD/MM/YYYY')
      if (data.foundationDate && dayjs(data.foundationDate).isValid()) data.foundationDate = timeAsDayjs(data.foundationDate, { applyTimezone: false }).format('DD/MM/YYYY')
      if (data.personType) setPersonType(data.personType as _personTypeEnum)

      if (formRef) formRef.setFieldsValue({ ...data })
    }

    onFill()
  }, [simulation.values])

  const onSubmit = async (values: ICustomerDataValues) => {
    const allSimulationValues = Object.assign({}, simulation.values)

    if (allSimulationValues.cpf) delete allSimulationValues.cpf
    if (allSimulationValues.cnpj) delete allSimulationValues.cnpj
    if (allSimulationValues.bacen) delete allSimulationValues.bacen
    if (allSimulationValues.birthdate) delete allSimulationValues.birthdate

    if (values.birthdate) values.birthdate = correctTimezone(String(values.birthdate))

    if (values.foundationDate) {
      values.foundationDate = correctTimezone(String(values.foundationDate))
      delete values.birthdate
    }

    if (values.cpf) values.cpf = format.onlyDigits(values.cpf)
    if (values.cnpj) values.cnpj = format.onlyCnpj(values.cnpj)
    if (values.income) values.income = format.formatMoneySend(String(values.income))
    if (!values.personType) values.personType = personType as _personTypeEnum

    const data: ICustomerDataValues = {
      ...allSimulationValues,
      ...values,
      personType: (values.personType ?? personType),
      simulationId: simulation.id || simulation.values?._id,
      currentStep: 3
    }
    dispatch(simulationValues({ ...data }, 3))
  }

  return (
    <div className='refinancing-content'>
      <h4 className="simulator-title-baseline" style={{ fontSize: 18, marginBottom: 24 }}>Dados do Cliente</h4>

      <Form
        layout="vertical"
        form={formRef}
        onFinish={onSubmit}
      >
        <Row>
          <Col className="py-1" lg={24} md={24} sm={24} xs={24}>
            <Form.Item
              name={personType === 'fisica' ? 'cpf' : 'cnpj'}
              label={<label className="simulator-label">{personType === 'fisica' ? 'CPF' : 'CNPJ'}</label>}
              className='refinancing-form-item'
              rules={[{ required: true, message: 'Digite um CPF/CNPJ válido' }]}
              required
            >
              <Input
                placeholder='Digite o CPF ou CNPJ'
                className='simulator-input ant-input'
                mask={personType === 'fisica' ? 'cpf' : 'cnpj'}
                disabled
              />
            </Form.Item>
          </Col>

          <Col className="py-1" lg={24} md={24} sm={24} xs={24}>
            <Form.Item
              name='name'
              label={<label className="simulator-label">Nome</label>}
              className='refinancing-form-item'
              rules={[{ required: true, message: 'Digite um nome válido' }]}
              required
            >
              <Input
                className='simulator-input ant-input'
                placeholder="Digite seu nome"
                disabled
              />
            </Form.Item>
          </Col>

          <Col className="py-1" lg={24} md={24} sm={24} xs={24}>
            <Form.Item
              name={personType === 'fisica' ? 'birthdate' : 'foundationDate'}
              label={<label className="simulator-label">{personType === 'fisica' ? 'Data de nascimento' : 'Data de fundação'}</label>}
              className='refinancing-form-item'
              rules={[{ required: true, message: 'Digite uma data válida' }]}
              required
            >
              <Datepicker
                className='simulator-input ant-input'
                id="birthdate"
                locale={locale}
                disabled
              />
            </Form.Item>
          </Col>

          <Col className="py-1" lg={24} md={24} sm={24} xs={24}>
            <Form.Item
              name='personType'
              label={<label className="simulator-label color-primary">Tipo de pessoa</label>}
              className='refinancing-form-item'
              rules={[{ required: true, message: 'Selecione um tipo de pessoa válido' }]}
              required
            >
              <Radio.Group
                className="simulator-radio"
                defaultValue="fisica"
                size="large"
                buttonStyle="outline"
              >
                <Radio.Button
                  id="simulator-radio-itens-fisica"
                  className="simulator-radio-itens-values"
                  value="fisica"
                  disabled
                >
                  Física
                </Radio.Button>

                <Radio.Button
                  id="simulator-radio-itens-juridica"
                  className="simulator-radio-itens-values"
                  value="juridica"
                  disabled
                >
                  Jurídica
                </Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col className="py-1" lg={24} md={24} sm={24} xs={24}>
            <Form.Item
              name='email'
              label={<label className="simulator-label">Email</label>}
              className='refinancing-form-item'
              rules={[{ required: true, message: 'Digite um email válido' }]}
              required
            >
              <Input
                className='simulator-input ant-input'
                placeholder="Digite seu email"
              />
            </Form.Item>
          </Col>

          <Col className="py-1" lg={24} md={24} sm={24} xs={24}>
            <Form.Item
              name='cellphone'
              label={<label className="simulator-label">Telefone</label>}
              className='refinancing-form-item'
              rules={[{ required: true, message: 'Digite um telefone válido' }]}
              required
            >
              <Input
                className='simulator-input ant-input'
                placeholder="Digite seu telefone"
                mask='phone'
              />
            </Form.Item>
          </Col>

          {personType === 'fisica' && (
            <Col className="py-1" lg={24} md={24} sm={24} xs={24}>
              <Form.Item
                name='employmentType'
                label={<label className="simulator-label">Ocupação</label>}
                className='refinancing-form-item'
                rules={[{ required: true, message: 'Selecione uma ocupação válida' }]}
                required
              >
                <Select
                  id="test-select-simulation-employmentType"
                  placeholder="Selecione uma opção"
                  className="unauth-inputs-center"
                >
                  <Select.Option id="test-select-simulation-employmentType-assalariado" value="assalariado">Assalariado(a)</Select.Option>
                  <Select.Option id="test-select-simulation-employmentType-funcionarioPublico" value="funcionarioPublico">Funcionário(a) Público(a)</Select.Option>
                  <Select.Option id="test-select-simulation-employmentType-aposentado" value="aposentado">Aposentado(a)</Select.Option>
                  <Select.Option id="test-select-simulation-employmentType-liberal" value="liberal">Profissional Liberal</Select.Option>
                  <Select.Option id="test-select-simulation-employmentType-autonomo" value="autonomo">Autônomo(a)</Select.Option>
                  <Select.Option id="test-select-simulation-employmentType-empresario" value="empresario">Empresário(a)</Select.Option>
                  <Select.Option id="test-select-simulation-employmentType-rural" value="rural">Produtor(a) Rural</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          )}

          <Col className="py-1" lg={24} md={24} sm={24} xs={24}>
            <Form.Item
              name='income'
              label={<label className="simulator-label">{personType === 'fisica' ? 'Renda familiar:' : 'Faturamento:'}</label>}
              className='refinancing-form-item'
              rules={[{ required: true, message: 'Digite uma renda válida' }]}
              required
            >
              <InputMoney
                id="income"
                className="simulator-input ant-input"
                placeholder='R$'
              />
            </Form.Item>
          </Col>
        </Row>

        <RefinancingButton handleClick={() => formRef.submit()} />
      </Form>
    </div>
  )
}
